export type EndedCampaignDistribution = {
  id: string;
  name: string;
  currency: string;
  campaign_type?: string | null;
  budget_total_cents: number;
  budget_remaining_cents: number;
};

export type EndedCampaignCurrencySummary = {
  currency: string;
  count: number;
  totalCents: bigint;
  averageCents: bigint;
  largestCampaign: { id: string; name: string; distributedCents: bigint };
};

// These are audited campaign earnings, not proof of a completed PIX withdrawal.
export function campaignDistributedCents(
  campaign: Pick<EndedCampaignDistribution, 'budget_total_cents' | 'budget_remaining_cents'>,
): number {
  return Math.max(0, campaign.budget_total_cents - campaign.budget_remaining_cents);
}

export function summarizeEndedCampaigns(
  campaigns: readonly EndedCampaignDistribution[],
): EndedCampaignCurrencySummary[] {
  const summaries = new Map<string, EndedCampaignCurrencySummary>();

  for (const campaign of campaigns) {
    // Legacy campaigns without a type are CPM; fixed prize pools are not earnings.
    if (campaign.campaign_type && campaign.campaign_type !== 'cpm') continue;

    const distributedCents = BigInt(campaignDistributedCents(campaign));
    const summary = summaries.get(campaign.currency);
    if (!summary) {
      summaries.set(campaign.currency, {
        currency: campaign.currency,
        count: 1,
        totalCents: distributedCents,
        averageCents: distributedCents,
        largestCampaign: { id: campaign.id, name: campaign.name, distributedCents },
      });
      continue;
    }

    summary.count += 1;
    summary.totalCents += distributedCents;
    const count = BigInt(summary.count);
    summary.averageCents = (summary.totalCents + count / 2n) / count;
    if (distributedCents > summary.largestCampaign.distributedCents) {
      summary.largestCampaign = { id: campaign.id, name: campaign.name, distributedCents };
    }
  }

  return [...summaries.values()];
}
